import React, { useEffect } from 'react'
import { useQuery } from '@apollo/client'
import { useParams } from 'react-router-dom'
import { UserCheck } from 'lucide-react'
import { Switch } from '@/components/ui/switch'
import { useMediaBuyingContext } from '@/context/media-buying-context'
import { PUBLISHER_NAMES } from '@/graphql/account-signals-queries'
import { WARM_AUDIENCES } from '@/graphql/warm-audiences-queries'
import { metaPublisherIdOf } from '@/components/mediaBuying/adCampaign/screens/AudienceTargeting/Settings/LanguageSettings'
import { exclusionStatusLine } from '@/utils/audience-exclusions'
import {
    WARM_AUDIENCE_ID,
    WARM_AUDIENCE_OPTION,
    WARM_AUDIENCE_TEXT,
    isWarmAudienceObjective,
    isWarmAudienceSelected,
    withWarmAudience,
} from '@/utils/warm-audiences'

interface WarmAudienceSource {
    audience_id: string
    kind: string
    label: string
    retention_days: number
    status: string
    approximate_count_lower: number | null
    message: string | null
}

interface WarmAudiencePanelProps {
    objective: string
    data: Record<string, any>
    onChange: (data: Record<string, any>) => void
    isEditMode?: boolean
}

/**
 * The lead form retargeting option of the retargeting stage. Shown only when the server offers it for the
 * licence and objective; the status comes from reads only, nothing is created before the campaign is
 * published. A choice carried over from a duplicated campaign is removed once the server reports the option
 * as not offered, so it is never sent without being shown.
 */
const WarmAudiencePanel: React.FC<WarmAudiencePanelProps> = ({ objective, data, onChange, isEditMode = false }) => {
    const { id: campaignId } = useParams()
    const { connectedPublishers } = useMediaBuyingContext()
    const hasConnection = !!connectedPublishers && Object.keys(connectedPublishers).length > 0
    const enabled = !isEditMode && isWarmAudienceObjective(objective) && hasConnection && !!campaignId

    const publishersResult = useQuery(PUBLISHER_NAMES, { fetchPolicy: 'cache-first', skip: !enabled })
    const publisherId = metaPublisherIdOf(connectedPublishers, publishersResult.data?.getPublishersFromPostgres)
    const { data: queryData } = useQuery(WARM_AUDIENCES, {
        variables: { publisherId, campaignId, objective: String(objective || '').toLowerCase() },
        fetchPolicy: 'network-only',
        skip: !enabled || !publisherId,
    })

    const result = queryData?.warmAudiences
    const source: WarmAudienceSource | undefined = result?.available
        ? (result.audiences || []).find((audience: WarmAudienceSource) => audience.audience_id === WARM_AUDIENCE_ID)
        : undefined
    const selected = isWarmAudienceSelected(data)
    const notOffered = enabled && !!result && !source && selected

    useEffect(() => {
        if (notOffered) onChange(withWarmAudience(data, false))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [notOffered])

    if (!enabled || !source) return null

    const switchId = 'warm-audience-lead-form'
    return (
        <div
            className="tw:mx-6 tw:flex tw:gap-3 tw:flex-col tw:bg-secondary-background tw:rounded-xs tw:p-3"
            data-testid="warm-audience-panel"
        >
            <div className="tw:flex tw:gap-1.5 tw:items-center">
                <UserCheck className="tw:w-6 tw:h-6 tw:text-gray-200" />
                <span className="tw:font-semibold">{WARM_AUDIENCE_TEXT.title}</span>
                <span className="tw:text-sm tw:text-gray-200">(optional)</span>
            </div>
            <div className="tw:flex tw:items-center tw:gap-2">
                <Switch
                    id={switchId}
                    checked={selected}
                    onCheckedChange={(checked: boolean) => onChange(withWarmAudience(data, checked))}
                />
                <label htmlFor={switchId} className="tw:text-sm tw:font-medium tw:cursor-pointer">
                    {WARM_AUDIENCE_OPTION.name}
                </label>
            </div>
            <p className="tw:text-sm tw:text-gray-200">{WARM_AUDIENCE_TEXT.description}</p>
            <span className="tw:text-xs tw:text-gray-200" data-testid="warm-audience-status">
                {exclusionStatusLine(source)}
            </span>
            <p className="tw:text-xs tw:text-gray-200">{WARM_AUDIENCE_TEXT.creationNote}</p>
        </div>
    )
}

export default WarmAudiencePanel
